/**
 * SQL Security Utilities
 * 
 * Provides utilities to prevent SQL injection attacks in PostgreSQL queries.
 */

/**
 * Escape special PostgreSQL LIKE/ILIKE characters to prevent pattern injection.
 * 
 * Escapes the following special characters:
 * - % (matches any sequence of characters)
 * - _ (matches any single character)  
 * - \ (escape character itself)
 * 
 * @param {string} str - The string to escape
 * @returns {string} - The escaped string safe for use in LIKE/ILIKE patterns
 * 
 * @example
 * const userInput = "test_user%";
 * const escaped = escapePostgresLike(userInput);
 * // escaped = "test\\_user\\%"
 * query.ilike('name', `%${escaped}%`);
 */
export const escapePostgresLike = (str) => {
  if (!str || typeof str !== 'string') return '';
  // Escape %, _, and \ by prefixing with backslash
  return str.replace(/[%_\\]/g, '\\$&');
};

/**
 * Sanitize user input for use in full-text search queries.
 * Removes characters that could break query syntax.
 * 
 * @param {string} str - The search query to sanitize
 * @returns {string} - The sanitized search query
 */
export const sanitizeSearchQuery = (str) => {
  if (!str || typeof str !== 'string') return '';
  // Remove potentially dangerous characters while preserving normal search terms
  return str.replace(/[;&|<>]/g, '').trim();
};

/**
 * Validate and sanitize an email address.
 * 
 * @param {string} email - The email to validate
 * @returns {{valid: boolean, sanitized: string}} - Validation result and sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { valid: false, sanitized: '' };
  }
  
  const sanitized = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  return {
    valid: emailRegex.test(sanitized),
    sanitized
  };
};

export default {
  escapePostgresLike,
  sanitizeSearchQuery,
  sanitizeEmail
};
